import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Formik, FormikProps } from "formik";
import * as yup from "yup";
import { Modal, TextArea } from "semantic-ui-react";

import ModalRightSideBarTemplate from "../../../../../../../App/views/ModalRightSideBarTemplate";
import {
  deskimoDayOfficeModalSelector,
  propertyDetailSelector,
} from "../../../../../../selectors/PropertySelector";
import { deskimoModalOfficeAction } from "../../../../../../actions/PropertyActions";
import SizeBox from "../../../../../../../../components/SizeBox";
import Label from "../../../../../../../../components/Label";
import ButtonCustom from "../../../../../../../../components/Button";
import { LocalizationContext } from "../../../../../../../../locales/Translation";
import { PUBLIC_URL } from "../../../../../../../App/configs/Constants";
import InputCustom from "../../../../../../../../components/Input";
import Divide from "../../../../../../../../components/Divide";
import AmenityItemSelect from "../../../../../../../../components/AmenityItemSelect";

import {
  officeCreateEditService,
  officeDetailsService,
} from "../../../../../../services/PropertyServices";
import { getPropertyAmenityListService } from "../../../../../../services/PropertyAmenityServices";
import {
  toastError,
  toastSuccess,
} from "../../../../../../../../components/Toast";
import {
  CREATE_TYPE,
  MINIMUM_BOOKING_TIME,
} from "../../../../../../configs/Constants";
import { AmenityItem, OfficeFormValues } from "../../types";
import Checkbox from "../../../../../../../../components/Checkbox";
import { omit } from "lodash";
import DropzoneComponentValidated from "../../../DetailsTab/Components/DropzoneComponentValidated";

import "./styles.scss";
import { AMENITY_LIST_FILTER } from "../../constants";
import DropdownCustom from "../../../../../../../../components/DropdownCustom";

interface Props {
  onRefresh: () => void;
}

// Validation Schema
const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("Day office name is required")
    .label("Office Name"),
  hourly_rate: yup
    .string()
    .required("Hourly rate is required")
    .label("Hourly Rate"),
  daily_rate: yup.string().when("available_for_daily_booking", {
    is: true,
    then: (daily_rate) => daily_rate.required().label("Daily Rate"),
    otherwise: (daily_rate) => daily_rate.notRequired().nullable(),
  }),
  monthly_rate: yup.string().label("Monthly Rate"),
  capacity: yup
    .string()
    .required("Capacity is required")
    .label("Capacity")
    .min(1, "Capacity cannot be less than 1"),
  images: yup
    .array()
    .required()
    .label("Photos")
    .min(1, "At least one image is required"),
});

const minBookingTimeOptions = MINIMUM_BOOKING_TIME();

const ModalCreateEditOffice: FC<Props> = ({ onRefresh }) => {
  // State
  const [loading, setLoading] = useState(false);
  const [amenities, setAmenities] = useState<AmenityItem[]>([]);
  const [officeDetail, setOfficeDetail] = useState<any>(null);

  // Hooks
  const { translations } = useContext(LocalizationContext);
  const dispatch = useDispatch();
  const { id: propertyId, companyId } = useParams<{
    id: string;
    companyId: string;
  }>();

  // Selectors
  const modal = useSelector(deskimoDayOfficeModalSelector);
  const propertyDetail = useSelector(propertyDetailSelector);

  // Computed values
  const isEditMode = modal?.dayOfficeId && modal?.isOpen;
  const modalTitle =
    modal?.type === CREATE_TYPE ? "Create Day Office" : "Edit Day Office";
  const currency = isEditMode
    ? officeDetail?.currency
    : propertyDetail?.currency;
  const currentCompanyId = modal?.companyId || companyId;
  const currentPropertyId = modal?.propertyId || propertyId;

  // Form initial values
  const initialValues: OfficeFormValues = useMemo(
    () => ({
      name: officeDetail?.name || "",
      hourly_rate: officeDetail?.hourly_rate || "",
      is_available_for_daily_booking:
        officeDetail?.is_available_for_daily_booking || 1,
      daily_rate: officeDetail?.daily_rate || "",
      monthly_rate: officeDetail?.monthly_rate || "",
      booking_requirement: officeDetail?.booking_requirement || "",
      minimum_booking_time: officeDetail?.minimum_booking_time || 0,
      capacity: officeDetail?.capacity || "",
      images: officeDetail?.images || [],
      area: officeDetail?.area || "",
    }),
    [officeDetail]
  );

  // Utility functions
  const transformAmenityFromAPI = (amenity: AmenityItem) => ({
    id: amenity.id,
    name: amenity.name,
    icon_key: amenity.file_key || amenity.image_url,
    is_chosen: amenity.is_chosen || false,
    is_free: amenity.is_free !== undefined ? amenity.is_free : true,
    uri: amenity.uri || amenity.image_url,
  });

  // API calls
  const fetchOfficeDetail = useCallback(async () => {
    if (!isEditMode) return;

    try {
      const { data } = await officeDetailsService(
        currentCompanyId,
        modal.dayOfficeId
      );
      setOfficeDetail(data);
      setAmenities(data.amenities.map(transformAmenityFromAPI));
    } catch (error) {
      console.error("Failed to fetch office details:", error);
      toastError("Failed to load office details");
    }
  }, [isEditMode, currentCompanyId, modal?.dayOfficeId]);

  const fetchAmenities = useCallback(async () => {
    if (isEditMode) return;

    try {
      const { data } = await getPropertyAmenityListService(AMENITY_LIST_FILTER);
      setAmenities(data.amenities.map(transformAmenityFromAPI));
    } catch (error) {
      console.error("Failed to fetch amenities:", error);
      toastError("Failed to load amenities");
    }
  }, [isEditMode]);

  // Effects
  useEffect(() => {
    if (modal?.isOpen) {
      (async () => await fetchOfficeDetail())();
    } else {
      setOfficeDetail(null);
    }
  }, [modal?.isOpen, fetchOfficeDetail]);

  useEffect(() => {
    (async () => await fetchAmenities())();
  }, [fetchAmenities]);

  // Event handlers
  const handleClose = useCallback(() => {
    setAmenities((prev) =>
      prev.map((item) => ({
        ...item,
        is_chosen: false,
        is_free: true,
      }))
    );
    dispatch(deskimoModalOfficeAction({ isOpen: false }));
    setOfficeDetail(null);
  }, [dispatch]);

  const handleSubmit = useCallback(
    async (values: OfficeFormValues) => {
      const restOfValues = omit(values, "area");
      const payload = {
        ...restOfValues,
        is_available_for_daily_booking: values.is_available_for_daily_booking
          ? 1
          : 0,
        capacity: parseInt(values.capacity),
        amenities,
        property_id: currentPropertyId,
        company_id: currentCompanyId,
      };

      setLoading(true);

      try {
        if (modal?.type === CREATE_TYPE) {
          await officeCreateEditService(payload);
          toastSuccess("Office created successfully!");
        } else {
          if (!modal?.dayOfficeId) {
            throw new Error("Office ID is required for editing");
          }
          await officeCreateEditService({
            ...payload,
            day_office_id: modal.dayOfficeId,
          });
          toastSuccess("Office updated successfully!");
        }

        handleClose();
        onRefresh();
      } catch (error: any) {
        console.error("Failed to save office:", error);
        toastError(error?.status?.message || "Failed to save office");
      } finally {
        setLoading(false);
      }
    },
    [
      amenities,
      currentPropertyId,
      currentCompanyId,
      modal,
      handleClose,
      onRefresh,
    ]
  );

  const handleValidation = (values: OfficeFormValues) => {
    const errors: Partial<Record<keyof OfficeFormValues, string>> = {};

    if (!values.daily_rate) {
      errors.daily_rate =
        "Daily rate is required when available for daily booking.";
    }

    return errors;
  };

  const handleAmenityToggle = (
    id: string | number,
    field: "is_chosen" | "is_free"
  ) => {
    setAmenities((prev) =>
      prev.map((item) =>
        parseInt(String(item.id)) === parseInt(String(id))
          ? { ...item, [field]: !item[field] }
          : item
      )
    );
  };

  const handleImageReorder = (
    sourceIndex: number,
    targetIndex: number,
    images: any[],
    setFieldValue: FormikProps<OfficeFormValues>["setFieldValue"]
  ) => {
    if (sourceIndex === targetIndex) return;

    const newImages = [...images];
    const [movedImage] = newImages.splice(sourceIndex, 1);
    newImages.splice(targetIndex, 0, movedImage);

    const reorderedImages = newImages.map((item, index) => ({
      ...item,
      weight: index,
    }));

    setFieldValue("images", reorderedImages);
  };

  const handleImageDelete = (
    imageId: any,
    images: any[],
    setFieldValue: FormikProps<OfficeFormValues>["setFieldValue"]
  ) => {
    const filteredImages = images.filter((image) => image?.id !== imageId);
    setFieldValue("images", filteredImages);
  };

  const handleImageAdd = (
    newImages: any[],
    setFieldValue: FormikProps<OfficeFormValues>["setFieldValue"]
  ) => {
    setFieldValue("images", [...newImages]);
  };

  // Render helpers
  const renderHeader = () => (
    <Modal.Header>
      <div>{modalTitle}</div>
      <ButtonCustom
        onClick={handleClose}
        className="m-0 p-0 none-bg close-modal"
      >
        <>
          {translations.settings.close}
          <img
            src={`${PUBLIC_URL}/assets/icons/icon-close-grey.svg`}
            alt="close"
          />
        </>
      </ButtonCustom>
    </Modal.Header>
  );

  const renderFormFields = (formik: FormikProps<OfficeFormValues>) => {
    const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
      formik;

    return (
      <>
        <InputCustom
          label="Office Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.name}
          error={errors.name}
          name="name"
          labelRequired
        />

        <InputCustom
          labelRequired
          containerClass="flex-1"
          label="Hourly Rate"
          value={values.hourly_rate}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.hourly_rate}
          error={errors.hourly_rate}
          name="hourly_rate"
          inputLeftIcon={currency}
          inputLeftIconBorder
          removeLetter
        />

        <div className="d-flex align-items-center justify-content-between mb-16px">
          <Checkbox
            checked={values.is_available_for_daily_booking}
            onChange={(_, data) => {
              setFieldValue("is_available_for_daily_booking", data.checked);
            }}
            label="Available for daily booking"
            reverseText={true}
          />
        </div>

        {values.is_available_for_daily_booking && (
          <InputCustom
            labelRequired
            containerClass="flex-1"
            label={translations.dailyRate}
            value={values.daily_rate}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.daily_rate}
            error={errors.daily_rate}
            name="daily_rate"
            inputLeftIcon={currency}
            inputLeftIconBorder
            removeLetter
          />
        )}

        <InputCustom
          containerClass="flex-1"
          label={translations.monthlyRate}
          value={values.monthly_rate}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.monthly_rate}
          error={errors.monthly_rate}
          name="monthly_rate"
          inputLeftIcon={currency}
          inputLeftIconBorder
          removeLetter
        />
        <div>
          <InputCustom
            labelRequired
            label={translations.capacity}
            value={values.capacity}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.capacity}
            error={errors.capacity}
            name="capacity"
            inputLeftIcon="People"
            inputLeftIconBorder
            removeLetter
          />
          <Label
            fontSize={"12px"}
            label={"Minimum Booking Time"}
            className={"sm"}
          />
          <DropdownCustom
            onChange={(_: any, data: any) => {
              setFieldValue("minimum_booking_time", data.value);
            }}
            selectUI
            scrolling
            className={"w-100"}
            pointing
            value={values.minimum_booking_time}
            options={minBookingTimeOptions}
          />
        </div>
        <SizeBox height="16px" />
        <Label fontSize="12px" label="Booking requirements" className="sm" />
        <TextArea
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.booking_requirement}
          error={errors.booking_requirement}
          name={"booking_requirement"}
          className={"text-area"}
          rows={6}
          value={values.booking_requirement}
        />

        <SizeBox height="16px" />

        <Divide margin="0" type="horizontal" />

        <SizeBox height="16px" />

        <Label required fontSize="12px" label="Photos" className="sm" />
        <DropzoneComponentValidated
          containerClass="dropzone-box-container"
          onDragEnd={(_: any, sourceIndex: any, targetIndex: any) => {
            handleImageReorder(
              sourceIndex,
              targetIndex,
              values.images,
              setFieldValue
            );
          }}
          isDragDropChangeOrder
          value={values.images}
          deletePhotos={(id: any) => {
            handleImageDelete(id, values.images, setFieldValue);
          }}
          setPhotos={(data: any) => {
            handleImageAdd(data, setFieldValue);
          }}
          touched={touched.images}
          error={errors.images}
        />

        <Divide type="horizontal" margin="0" />
        <SizeBox height="16px" />

        <div>
          {amenities.map((amenity) => (
            <AmenityItemSelect
              key={amenity.id}
              item={amenity}
              onChangeCheck={(id: any) => handleAmenityToggle(id, "is_chosen")}
              onChangeFree={(id: any) => handleAmenityToggle(id, "is_free")}
            />
          ))}
        </div>
      </>
    );
  };

  const renderActions = (handleSubmit: () => void) => (
    <Modal.Actions>
      <ButtonCustom height="47px" onClick={handleClose}>
        {translations.settings.cancel}
      </ButtonCustom>
      <ButtonCustom
        loading={loading}
        disabled={loading}
        type="submit"
        className="primary"
        height="47px"
        onClick={handleSubmit}
      >
        {modal?.type === CREATE_TYPE
          ? `${translations.create}`
          : `${translations.settings.save}`}
      </ButtonCustom>
    </Modal.Actions>
  );

  return (
    <ModalRightSideBarTemplate
      trigger={null}
      onClose={handleClose}
      modalClassName="modal-edit-employee"
      open={modal?.isOpen}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validate={handleValidation}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <>
            {renderHeader()}
            <Modal.Content className="scroll">
              {renderFormFields(formik)}
            </Modal.Content>
            {renderActions(formik.handleSubmit)}
          </>
        )}
      </Formik>
    </ModalRightSideBarTemplate>
  );
};

export default ModalCreateEditOffice;
